import React, { FunctionComponent, ReactNode } from 'react'
import { useRouter } from 'next/router'
import { Text, PaymentIcon, useDate } from '@afterpaytouch/core'
import { PaymentMethodType } from '@afterpaytouch/portal-api'
import { CardBrand, SupportedLocale } from '@afterpay/types'
import { useTranslation } from 'next-i18next'
import { OrderPaymentResponses, OrderPaymentResponseStatus, OrderRefundResponse } from '@afterpaytouch/portal-api/consumer/ordertransactions/types'
import { Money } from '../Money'
import { useCardIconName } from '../../utils/cardIconName'
import { maskCreditCardNumber } from '../../utils/card'
import { formatBank } from '../../utils/bankAccount'
import { isRefundDisplayNameReturned, OrderScheduleLineItemAuxiliary, OrderScheduleLineItemTypes, isPayment as checkIsPayment } from './utils'

const I18N_NAMESPACE = ['common', 'orders']

interface OrderScheduleInstallmentLineProps {
  type: OrderScheduleLineItemTypes
  item: OrderPaymentResponses | OrderRefundResponse | OrderScheduleLineItemAuxiliary
}

export const OrderScheduleInstallmentLine: FunctionComponent<OrderScheduleInstallmentLineProps> = ({ item, type }) => {
  const testNameSpace = 'order-schedule-line'
  const router = useRouter()
  const locale = router?.locale as SupportedLocale
  const { formatUnixDate } = useDate({ locale, day: '2-digit', year: 'numeric' })
  const { t } = useTranslation(I18N_NAMESPACE)
  const isPayment = type === OrderScheduleLineItemTypes.Payment
  const isRefund = type === OrderScheduleLineItemTypes.Refund
  const isBank = item?.paymentMethodType === PaymentMethodType.ACH_BANK_ACCOUNT
  const isApplePay = item?.paymentMethodType === PaymentMethodType.APPLE_PAY
  const isCashAppPay = item?.paymentMethodType === PaymentMethodType.CASH_APP_PAY

  const cardIcon = useCardIconName(item?.cardBrand as CardBrand)
  const isRefundInformationReturned = isRefundDisplayNameReturned(item)

  const renderPaymentMethodIcon = (): ReactNode => {
    if (isBank) {
      return <PaymentIcon.GenericBank />
    }
    if (isApplePay) {
      return <PaymentIcon name='Applepay' />
    }
    if (isCashAppPay) {
      return <PaymentIcon.CashPay size={'Medium'} />
    }
    if (cardIcon != null) {
      return <PaymentIcon name={cardIcon} />
    }
    return <PaymentIcon.GenericBank />
  }

  const renderPaymentMethodName = (): ReactNode => {
    if (!isRefundInformationReturned) {
      return (
        <Text renderAs='span' size='S' testNameSpace={`${testNameSpace}-payment-refund`} color={'Gray40'}>
          {'••••••••'}
        </Text>
      )
    }
    if (isBank) {
      // @ts-ignore: OPERATION BLEED STOPPER
      return formatBank(item.displayName).map((bankDetail, index) => (
        <Text renderAs='span' key={bankDetail} size='S' testNameSpace={`${testNameSpace}-${index}-bank`}>
          {bankDetail}
        </Text>
      ))
    }
    if (isApplePay) {
      return (
        <Text renderAs='span' size='S' testNameSpace={`${testNameSpace}-applePay`}>
          {/* @ts-ignore: OPERATION BLEED STOPPER */}
          {maskCreditCardNumber(item.displayName)}
        </Text>
      )
    }
    if (isCashAppPay) {
      return (
        <Text size='S' testNameSpace={`${testNameSpace}-cashAppPay`}>
          {item.displayName}
        </Text>
      )
    }
    return (
      <Text renderAs='span' size='S' testNameSpace={`${testNameSpace}-card`}>
        {/* @ts-ignore: OPERATION BLEED STOPPER */}
        {maskCreditCardNumber(item.displayName)}
      </Text>
    )
  }
  return (
    <>
      <Text renderAs='span' size='S' testNameSpace={`${testNameSpace}-date`}>
        {formatUnixDate(item.createdDate)}
      </Text>
      <div className='flex gap-2' data-testid={`${testNameSpace}-payment`}>
        {isPayment || isRefund ? (
          <>
            {renderPaymentMethodIcon()}
            <div>
              <div className='flex flex-col'>{renderPaymentMethodName()}</div>
            </div>
          </>
        ) : (
          <Text renderAs='span' size='S' testNameSpace={`${testNameSpace}-auxiliary`}>
            {/* @ts-ignore: OPERATION BLEED STOPPER */}
            {t(item?.displayName)}
          </Text>
        )}
      </div>
      <div className='flex flex-col text-right' data-testid={`${testNameSpace}-amount`}>
        <Text renderAs='span' size='S' testNameSpace={`${testNameSpace}-amount`}>
          {isRefund && '+'}
          <Money size='S' value={item.amount} />
        </Text>
        {isPayment && checkIsPayment(item) && item.status === OrderPaymentResponseStatus.PENDING && (
          <Text renderAs='span' size='S' testNameSpace={`${testNameSpace}-pending`}>
            {t('orders:orderSchedule:paymentPending')}
          </Text>
        )}
        {isRefund && (
          <Text renderAs='span' size='S' testNameSpace={`${testNameSpace}-refund`}>
            {t('orders:orderSchedule:refund')}
          </Text>
        )}
      </div>
    </>
  )
}
